<template>
  <ScrollArea class="nav-panel" :class="{ compact: variant === 'secondary' }" fade>
    <router-link
      class="link"
      v-for="link of links"
      :key="link.appRoute"
      :id="link.id"
      :to="{
        name: link.appRoute,
        params: link.params,
        query: link.query,
      }"
    >
      <Text class="text" :variant="variant === 'primary' ? 'label-large' : 'label-medium'" as="span">
        <Icon v-if="link.icon" :name="link.icon" size="small" />
        {{ link.title }}
        <Text v-if="link.count !== undefined" variant="label-small" as="span" class="count">{{ link.count }}</Text>
      </Text>
      <div class="bottom-line"></div>
    </router-link>
  </ScrollArea>
</template>

<script setup lang="ts" generic="T">
import { Icon, ScrollArea, Text } from '@tallo/design-system';

import { NavPanelLink } from './nav-panel-link.interface';

withDefaults(
  defineProps<{
    links: NavPanelLink[];
    variant?: 'primary' | 'secondary';
  }>(),
  {
    variant: 'primary',
  },
);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.nav-panel {
  // Room for the focus ring around the first and last labels: the panel clips at its own edges,
  // and the negative margin keeps the links exactly where they were.
  --focus-ring-room: 0.375rem;

  display: flex;
  overflow: auto;
  height: 3.5rem;
  gap: var(--t-spacing-xl);
  padding-inline: var(--focus-ring-room);
  margin-inline: calc(-1 * var(--focus-ring-room));

  &.compact {
    height: 3.5rem;
  }
}

.link {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  color: var(--t-text-color-tertiary);
  text-wrap: nowrap;

  // The link fills the panel's height, so a ring around it is clipped top and bottom: ring the label.
  &:focus-visible {
    outline: none;

    .text {
      outline: 2px solid var(--t-color-border-primary-hover);
      outline-offset: 0.25rem;
      border-radius: var(--t-border-radius-xs);
    }
  }

  .text {
    display: inline-flex;
    align-items: center;
    gap: var(--t-spacing-xs);
  }

  // The count reads as a note beside the title, and does not brighten with it
  .count {
    color: var(--t-text-color-tertiary);
  }

  &.router-link-active {
    color: var(--t-text-color-primary);

    .text {
      margin-top: auto;
      margin-bottom: -0.125rem;
    }

    .bottom-line {
      width: 100%;
      height: 0.125rem;
      border-radius: var(--t-border-radius-xs);
      background-color: var(--t-text-color-primary);
      margin-top: auto;
    }
  }
}
</style>
